import { useEffect, useRef } from "react";
import type { RadioStation } from "@/contexts/RadioContext";

const FRAMES = [
  "/radio-dance/frame-1.png",
  "/radio-dance/frame-2.png",
  "/radio-dance/frame-3.png",
  "/radio-dance/frame-4.png",
];
const FRAME_INTERVAL_MS = 320; // ~3 fps - readable on lock screen
const MIN_CYCLES = 3;
const MAX_CYCLES = 5;

interface BatteryLike {
  level: number;
  charging: boolean;
  addEventListener?: (t: string, cb: () => void) => void;
  removeEventListener?: (t: string, cb: () => void) => void;
}

interface NavigatorWithBattery extends Navigator {
  getBattery?: () => Promise<BatteryLike>;
  connection?: { saveData?: boolean; effectiveType?: string; addEventListener?: (event: string, cb: () => void) => void; removeEventListener?: (event: string, cb: () => void) => void };
}

/**
 * Animates the lock-screen / Now Playing artwork as a flipbook of dance frames
 * when radio playback starts. Industry-standard MediaSession API.
 *
 * Power/accessibility safeguards:
 *  - Disabled when prefers-reduced-motion is set
 *  - Disabled when device is on low battery (<20%) and not charging
 *  - Disabled when Save-Data is enabled
 *  - Animates 3–5 cycles then settles on a static "playing" frame to avoid
 *    continuous CPU/GPU work and battery drain
 */
export function useRadioMediaSession(
  station: RadioStation | null,
  isPlaying: boolean,
  onStop: () => void,
  onPause: () => void,
  onPlay: () => void,
) {
  const animTimer = useRef<number | null>(null);
  const stationKey = station?.stationuuid ?? null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("mediaSession" in navigator)) return;
    if (!station || !isPlaying) {
      navigator.mediaSession.playbackState = "paused";
      if (animTimer.current) {
        window.clearInterval(animTimer.current);
        animTimer.current = null;
      }
      return;
    }

    const nav = navigator as NavigatorWithBattery;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const saveData = !!nav.connection?.saveData;

    let cancelled = false;
    let battery: BatteryLike | null = null;

    const buildMetadata = (artworkSrc: string) => {
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: station.name || "Radio",
          artist: station.country || station.tags?.split(",")[0] || "Live Radio",
          album: "Elefa Radio",
          artwork: [
            { src: artworkSrc, sizes: "512x512", type: "image/png" },
          ],
        });
      } catch {
        /* MediaMetadata unsupported */
      }
    };

    const startAnimation = () => {
      const cycles = Math.floor(Math.random() * (MAX_CYCLES - MIN_CYCLES + 1)) + MIN_CYCLES;
      const totalFrames = cycles * FRAMES.length;
      let i = 0;
      buildMetadata(FRAMES[0]);
      animTimer.current = window.setInterval(() => {
        i += 1;
        if (i >= totalFrames) {
          if (animTimer.current) window.clearInterval(animTimer.current);
          animTimer.current = null;
          // Settle on a happy static frame
          buildMetadata(FRAMES[2]);
          return;
        }
        buildMetadata(FRAMES[i % FRAMES.length]);
      }, FRAME_INTERVAL_MS);
    };

    const decideAndStart = (b: BatteryLike | null) => {
      if (cancelled) return;
      const lowPower = b ? b.level < 0.2 && !b.charging : false;
      if (reducedMotion || saveData || lowPower) {
        // Static artwork only
        buildMetadata(FRAMES[2]);
      } else {
        startAnimation();
      }
    };

    // Always set action handlers + initial state
    navigator.mediaSession.playbackState = "playing";
    try {
      navigator.mediaSession.setActionHandler("play", () => onPlay());
      navigator.mediaSession.setActionHandler("pause", () => onPause());
      navigator.mediaSession.setActionHandler("stop", () => onStop());
      // Live streams have no seek/track nav
      navigator.mediaSession.setActionHandler("previoustrack", null);
      navigator.mediaSession.setActionHandler("nexttrack", null);
      navigator.mediaSession.setActionHandler("seekto", null);
    } catch {
      /* ignore unsupported actions */
    }

    if (nav.getBattery) {
      nav.getBattery().then((b) => {
        battery = b;
        decideAndStart(b);
      }).catch(() => decideAndStart(null));
    } else {
      decideAndStart(null);
    }

    return () => {
      cancelled = true;
      if (animTimer.current) {
        window.clearInterval(animTimer.current);
        animTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationKey, isPlaying]);
}
